import React, { useState, useEffect } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { cachedApiGet } from '../apiCache';



function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [activePath, setActivePath] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [departments, setDepartments] = useState([]);
  const [departmentsOpen, setDepartmentsOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState('documents');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const navigate = useNavigate();
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [managerDepartments, setManagerDepartments] = useState([]);
  const [managerBranches, setManagerBranches] = useState([]);
  const isManager = currentUser?.role_name === 'manager';
  const role = currentUser?.role_name;

  const isAdmin = role === 'admin';
  const isLimitedRole = ['manager', 'subadmin', 'user'].includes(role);

  useEffect(() => setActivePath(location.pathname), [location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = localStorage.getItem('id');

        const usersPromise = cachedApiGet(
          'adminDashboard:user:list',
          () => axiosInstance.get('/user/'),
          10 * 60 * 1000
        );
           
        const docsPromise = cachedApiGet(
          'adminDashboard:documents:list',
          () => axiosInstance.get('/all-documents/'),
          5 * 60 * 1000
      );

        const policiesPromise = cachedApiGet(
          'adminDashboard:policies:list',
          () => axiosInstance.get('/policies/'),
          5 * 60 * 1000
        );

        const rolesPromise = cachedApiGet(
          'adminDashboard:roles:list',
          () => axiosInstance.get('/roles/'),
          60 * 60 * 1000
        );

        const userPromise = userId
          ? cachedApiGet(
              `adminDashboard:user:self:${userId}`,
              () => axiosInstance.get(`/user/${userId}/`),
              2 * 60 * 1000
            )
          : Promise.resolve({ data: null });

        const [usersRes, docsRes, policiesRes, rolesRes, userRes] = await Promise.all([
          usersPromise,
          docsPromise,
          policiesPromise,
          rolesPromise,
          userPromise,
        ]);

        setUsers(usersRes.data);
        const docsData = Array.isArray(docsRes.data?.data)
          ? docsRes.data.data
          : Array.isArray(docsRes.data)
            ? docsRes.data
            : [];

        setDocuments(docsData);


        setInsurance(
          Array.isArray(policiesRes.data?.data) ? policiesRes.data.data : []
        );


        setRoles(rolesRes.data);
        setCurrentUser(userRes.data);
        setCurrentDate(format(new Date(), 'EEEE, dd MMM yyyy'));
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const departmentsPromise = cachedApiGet(
      'adminDashboard:department:list',
      () => axiosInstance.get('/department/'),
      10 * 60 * 1000
    );

    departmentsPromise
      .then((res) => setDepartments(res.data || []))
      .catch((err) => console.error(err));
  }, []);


  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const visibleDepartments = isAdmin
  ? departments
  : managerDepartments;

  useEffect(() => {
    if (isLimitedRole) {
      const userId = localStorage.getItem('id');

      cachedApiGet(
        `adminDashboard:user-departments-branches:${userId || 'anon'}`,
        () => axiosInstance.get('/user-departments-branches/'),
        10 * 60 * 1000
      )
        .then((res) => {
          setManagerDepartments(res.data.departments || []);
          setManagerBranches(res.data.branches || []);
        })
        .catch(console.error);
    }
  }, [isLimitedRole]);

  
  


  return (
    <div className="dashboard-wrapper">
      {/* Left Sidebar */}
      <div className="dashboard-sidebar">
  <div className="sidebar-header">
    <img src="/logo.png" alt="Logo" className="sidebar-logo" />
  </div>

  <nav className="sidebar-nav">
    <Link
      to="/admin-dashboard"
      className={`sidebar-link ${activePath === '/admin-dashboard' ? 'active' : ''}`}
    >
      <i className="fas fa-home"></i> Dashboard
    </Link>

    {currentUser?.role_name === 'admin' && (
      <Link
        to="/users"
        className={`sidebar-link ${activePath === '/users' ? 'active' : ''}`}
      >
        <i className="fas fa-users"></i> Users
      </Link>
    )}

    {/* Simplified Departments Link */}
    <Link
      to="/departments"
      className={`sidebar-link ${activePath === '/departments' ? 'active' : ''}`}
    >
      <i className="fas fa-file-alt"></i> Departments
    </Link>
  </nav>
</div>


      <div
        className="dashboard-main"
        style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
          padding: '20px',
        }}
      >
        <div className="dashboard-content" style={{ flex: 3 }}>
          <h2>Dashboard</h2>
          <p style={{ color: '#888', marginTop: '-5px', marginBottom: '20px' }}>{currentDate}</p>

  <div
    className="dashboard-cards"
    style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}
  >
    {/* Users Card */}
    {isAdmin && (
  <motion.div className="dashboard-card users-card" whileHover={{ scale: 1.02 }}>
    <div className="card-header">
      <h3>Users</h3>
      <button className="view-all-btn" onClick={() => navigate('/users')}>
        View All
      </button>
    </div>

    <div className="user-boxes">
      {users.slice(0, 5).map((user) => (
        <div className="user-box" key={user.id}>
          <div className="user-initial">
            {user.first_name[0]}
            {user.last_name[0]}
          </div>
          <div className="user-name">
            {user.first_name} {user.last_name}
          </div>
          <div className="user-role">{user.role_name}</div>
        </div>
      ))}
    </div>
  </motion.div>
)}


    {/* Department Card */}
    <motion.div className="dashboard-card department-card" whileHover={{ scale: 1.02 }}>
  <div className="card-header">
    <h3>Departments</h3>
    <button className="view-all-btn" onClick={() => navigate('/departments')}>
      View All
    </button>
  </div>

  <div className="user-boxes">
    {visibleDepartments.slice(0, 5).map((dept) => (
      <div className="user-box" key={dept.id}>
        <div className="user-initial">{dept.name[0]}</div>
        <div className="user-name">{dept.name}</div>
      </div>
    ))}

    {visibleDepartments.length === 0 && (
      <p style={{ padding: '10px', color: '#888' }}>
        No departments assigned
      </p>
    )}
  </div>
</motion.div>


{isLimitedRole && (
  <motion.div className="dashboard-card department-card" whileHover={{ scale: 1.02 }}>
    <div className="card-header">
      <h3>Branches</h3>
      <button className="view-all-btn" onClick={() => navigate('/departments')}>
        View All
      </button>
    </div>

    <div className="user-boxes">
      {managerBranches.slice(0, 5).map((branch) => (
        <div className="user-box" key={branch.id}>
          <div className="user-initial">{branch.name[0]}</div>
          <div className="user-name">{branch.name}</div>
          <div className="user-role">{branch.department_name}</div>
        </div>
      ))}

      {managerBranches.length === 0 && (
        <p style={{ padding: '10px', color: '#888' }}>
          No branches assigned
        </p>
      )}
    </div>
  </motion.div>
)}


  </div>

        </div>

        {/* Right Sidebar with Profile */}
        <div
          className="dashboard-sidebar-right"
          style={{
            flex: 1,
            borderRadius: '25px',
            padding: '15px',
            background: 'rgba(245, 245, 245)',
            height: 'fit-content',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            position: 'relative',
          }}
        >
          {/* Profile + Logout Section */}
          <div
            className="profile-section"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <div>
              <strong>{currentUser?.first_name} {currentUser?.last_name}</strong>
              <p style={{ margin: 0, fontSize: '12px', color: '#777' }}>
                {currentUser?.role_name}
              </p>
            </div>
            <i
              className="fas fa-sign-out-alt"
              style={{ cursor: 'pointer', color: '#f47522', fontSize: '18px' }}
              onClick={() => setLogoutConfirm(true)}
              title="Logout"
            ></i>
          </div>

          {/* Logout Confirmation Modal */}
          {logoutConfirm && (
            <div
              className="logout-popup"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  background: '#fff',
                  padding: '30px',
                  borderRadius: '15px',
                  textAlign: 'center',
                  width: '300px',
                }}
              >
                <h4>Confirm Logout</h4>
                <p>Are you sure you want to log out?</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '15px' }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: '#f47522',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    Yes, Logout
                  </button>
                  <button
                    onClick={() => setLogoutConfirm(false)}
                    style={{
                      background: '#ccc',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Meter Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '10px' }}>
            <button className="doc-pol" onClick={() => setSelectedMeter('documents')}>
              Documents
            </button>
            <button className="doc-pol" onClick={() => setSelectedMeter('policies')}>
              Policies
            </button>
          </div>

          {/* Meter Card */}
          <div
            className="meter-card"
            style={{
              background: '#f5f5f5',
              padding: '15px',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <h4 style={{ margin: 0 }}>
              {selectedMeter === 'documents' ? 'Documents' : 'Policies'}
            </h4>

            {/* Year navigation */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '-5px' }}>
              <button
                className="view-all-btn"
                onClick={() => setSelectedYear((y) => y - 1)}
                style={{ padding: '6px 10px' }}
              >
                Prev
              </button>
              <div style={{ fontWeight: 600, color: '#333' }}>
                {selectedYear}
              </div>
              <button
                className="view-all-btn"
                onClick={() => setSelectedYear((y) => y + 1)}
                style={{ padding: '6px 10px' }}
              >
                Next
              </button>
            </div>

            {Array.from({ length: 12 }).map((_, monthIndex) => {
  const monthName = new Date(0, monthIndex).toLocaleString('default', { month: 'short' });
  const data = selectedMeter === 'documents' ? documents : insurance;
  const color = selectedMeter === 'documents' ? '#f47522' : '#4caf50';

  // Filter data based on role and status
  const filteredData = selectedMeter === 'documents'
    ? isAdmin
      ? data.filter((doc) => doc.status === 'enabled') // Admin: all enabled
      : data.filter((doc) => doc.role_name === currentUser?.role_name) // Other roles: their role only
    : data; // Policies or other meters remain unchanged

  // Count items for this month in the selected year
  const count = filteredData.filter((item) => {
    const raw = item?.created_at;

    // API seems to return dates like: "30-06-2026" (DD-MM-YYYY)
    // or sometimes ISO. Handle both robustly.
    let d;
    if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('-').map(Number);
      d = new Date(yyyy, mm - 1, dd);
    } else {
      d = new Date(raw);
    }

    if (Number.isNaN(d?.getTime?.())) return false;
    return d.getFullYear() === selectedYear && d.getMonth() === monthIndex;
  }).length;


  const percentage = Math.min((count / 10) * 100, 100);

  return (
    <div key={monthIndex} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ width: '30px', fontSize: '12px' }}>{monthName}</span>
      <div
        style={{
          flex: 1,
          height: '8px',
          background: '#ddd',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: color,
            borderRadius: '5px',
          }}
        />
      </div>
      <span style={{ fontSize: '12px', width: '20px', textAlign: 'right' }}>{count}</span>
    </div>
  );
})}

          </div>

          {/* Sidebar Image */}
          <div className="sidebar-bottom-image" style={{ textAlign: 'center' }}>
            <img
              src="/side1.png"
              alt="Sidebar Bottom"
              style={{
                width: '50%',
                height: '190px',
                borderRadius: '15px',
                objectFit: 'cover',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
